import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { deleteAccount, logout } from "../redux/authSlice";
import { Lock, Shield, LogOut, Trash2, Power, AlertTriangle, ChevronLeft, Eye, EyeOff } from "lucide-react";

const FONT_CSS = `@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');`;

const T = {
  bg: "#ffffff",
  surface: "#fafafa",
  surface2: "#f5f5f0",
  border: "#e5e5e5",
  border2: "#d4d4d4",
  text: "#0a0a0a",
  textSoft: "#525252",
  muted: "#a3a3a3",
  accent: "#0a0a0a",
  danger: "#b91c1c",
  dangerSoft: "#fef2f2",
  dangerBorder: "#fecaca",
  warnBg: "#fffbeb",
  warnBorder: "#fde68a",
  warnText: "#92400e",
  serif: "'Cormorant Garamond', serif",
  sans: "'Montserrat', sans-serif",
};

const maskEmail = (email: string) => {
  if (!email) return "";
  const [user, domain] = email.split("@");
  if (!domain) return email;
  const masked = user.length <= 2 ? user + "\u00b7\u00b7\u00b7" : user.slice(0, 2) + "\u00b7\u00b7\u00b7";
  return masked + "@" + domain;
};

const LoginSecurity: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { userId } = useParams<{ userId: string }>();

  const [editingPassword, setEditingPassword] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    try {
      setLoading(true);
      await dispatch(deleteAccount()).unwrap();
      await dispatch(logout()).unwrap();
      navigate("/AuthSwitch");
    } catch (err: any) {
      alert(err?.message || "Failed to delete account.");
    } finally {
      setLoading(false);
    }
  };

  const firstName = user?.firstName || "";
  const lastName = user?.lastName || "";
  const email = user?.emailAddress ? maskEmail(user.emailAddress) : "";

  return (
    <div style={S.root}>
      <style>{FONT_CSS}</style>

      {/* ── Header ── */}
      <header style={S.header}>
        <div style={S.headerLeft} onClick={() => navigate(-1)}>
          <ChevronLeft size={20} color="#e8e8e8"/>
          <span style={S.backLabel}>Back</span>
        </div>
        <span style={S.headerBrand}>EBuOS</span>
        <div style={{ width: 60 }}/>
      </header>

      {/* ── Content ── */}
      <div style={S.content}>

        {/* Page title */}
        <div style={S.titleBlock}>
          <h1 style={S.pageTitle}>Login & Security</h1>
          <p style={S.pageSub}>Manage your password, authentication, and account status.</p>
        </div>

        {/* User info */}
        <div style={S.userCard}>
          <div style={S.userAvatar}>
            <span style={S.userInitial}>{firstName?.[0]?.toUpperCase() || "U"}</span>
          </div>
          <div>
            <div style={S.userName}>{firstName} {lastName}</div>
            <div style={S.userEmail}>{email}</div>
          </div>
        </div>

        {/* ── Password ── */}
        <div style={S.card}>
          <div style={S.cardHeader}>
            <div style={S.iconRow}>
              <div style={S.iconWrap}><Lock size={16} color={T.text}/></div>
              <div>
                <div style={S.cardTitle}>Password</div>
                <div style={S.cardSub}>Last changed 30+ days ago</div>
              </div>
            </div>
            {!editingPassword && (
              <button style={S.editBtn} onClick={() => setEditingPassword(true)}>EDIT</button>
            )}
          </div>

          <AnimatePresence>
            {editingPassword && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
                style={{ overflow: "hidden" }}>
                <div style={S.pwBox}>
                  <div style={{ position: "relative" }}>
                    <input type={showPw ? "text" : "password"} placeholder="New password" style={S.input}/>
                    <button onClick={() => setShowPw(v => !v)} style={S.eyeBtn}>
                      {showPw ? <EyeOff size={16} color={T.muted}/> : <Eye size={16} color={T.muted}/>}
                    </button>
                  </div>
                  <input type="password" placeholder="Confirm password" style={S.input}/>
                  <div style={S.pwActions}>
                    <button style={S.cancelBtn} onClick={() => setEditingPassword(false)}>CANCEL</button>
                    <button style={S.primaryBtn}>UPDATE PASSWORD</button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── 2FA ── */}
        <div style={S.card}>
          <div style={S.cardHeader}>
            <div style={S.iconRow}>
              <div style={S.iconWrap}><Shield size={16} color={T.text}/></div>
              <div>
                <div style={S.cardTitle}>Two-Factor Authentication</div>
                <div style={S.cardSub}>Add an extra layer of security to your account</div>
              </div>
            </div>
            <button style={S.editBtn}>SETUP</button>
          </div>
        </div>

        {/* ── Sessions ── */}
        <div style={S.card}>
          <div style={S.cardHeader}>
            <div style={S.iconRow}>
              <div style={S.iconWrap}><LogOut size={16} color={T.text}/></div>
              <div>
                <div style={S.cardTitle}>Active Sessions</div>
                <div style={S.cardSub}>Manage devices logged into your account</div>
              </div>
            </div>
            <button style={S.editBtn}>VIEW</button>
          </div>
        </div>

        {/* ── Danger Zone ── */}
        <div style={S.dangerSection}>
          <span style={S.dangerLabel}>DANGER ZONE</span>

          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={() => setShowDeactivateConfirm(true)}
            style={S.deactivateBtn}>
            <Power size={16} style={{ marginRight: 8 }}/>
            DEACTIVATE ACCOUNT
          </motion.button>

          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={() => setShowDeleteConfirm(true)}
            style={S.deleteBtn}>
            <Trash2 size={16} style={{ marginRight: 8 }}/>
            {loading ? "DELETING..." : "DELETE ACCOUNT"}
          </motion.button>
        </div>
      </div>

      {/* ── Confirm Overlays ── */}
      <AnimatePresence>
        {(showDeleteConfirm || showDeactivateConfirm) && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={S.overlay}
            onClick={() => { setShowDeleteConfirm(false); setShowDeactivateConfirm(false); }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}
              transition={{ duration: 0.25 }}
              style={showDeleteConfirm ? S.alertCardDanger : S.alertCardWarn}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              <AlertTriangle size={32} color={showDeleteConfirm ? T.danger : T.warnText} style={{ marginBottom: 12 }}/>
              <h3 style={{ ...S.alertTitle, color: showDeleteConfirm ? T.danger : T.warnText }}>
                {showDeleteConfirm ? "Delete Account?" : "Deactivate Account?"}
              </h3>
              <p style={S.alertText}>
                {showDeleteConfirm
                  ? "This action is permanent and cannot be undone. All data will be lost."
                  : "Your account will be paused. You can reactivate anytime by logging in."}
              </p>
              <div style={S.alertActions}>
                <button style={S.alertCancel} onClick={() => { setShowDeleteConfirm(false); setShowDeactivateConfirm(false); }}>
                  CANCEL
                </button>
                <button
                  style={showDeleteConfirm ? S.alertDanger : S.alertWarn}
                  onClick={showDeleteConfirm ? handleDelete : () => setShowDeactivateConfirm(false)}>
                  {showDeleteConfirm ? "DELETE" : "DEACTIVATE"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LoginSecurity;

/* ─── Styles ─────────────────────────────────────────────── */
const S: { [key: string]: React.CSSProperties } = {
  root: {
    minHeight: "100vh",
    background: T.bg,
    fontFamily: T.sans,
  },

  /* Header — dark */
  header: {
    position: "sticky",
    top: 0,
    zIndex: 50,
    background: "rgba(0,0,0,0.95)",
    backdropFilter: "blur(12px)",
    borderBottom: "1px solid #1c1c1c",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 20px",
    height: 56,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    cursor: "pointer",
  },
  backLabel: {
    color: "#e8e8e8",
    fontSize: 12,
    fontWeight: 400,
    letterSpacing: "0.05em",
    fontFamily: T.sans,
  },
  headerBrand: {
    position: "absolute" as const,
    left: "50%",
    transform: "translateX(-50%)",
    color: "#ffffff",
    fontWeight: 300,
    letterSpacing: "0.3em",
    fontSize: 14,
    fontFamily: T.serif,
  },

  /* Content */
  content: {
    maxWidth: 580,
    margin: "0 auto",
    padding: "32px 20px 80px",
  },

  /* Title */
  titleBlock: {
    marginBottom: 28,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: 300,
    color: T.text,
    fontFamily: T.serif,
    letterSpacing: "0.04em",
    margin: 0,
    marginBottom: 6,
  },
  pageSub: {
    fontSize: 13,
    color: T.textSoft,
    margin: 0,
    lineHeight: 1.5,
  },

  /* User card */
  userCard: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "20px 0",
    borderBottom: "1px solid " + T.border,
    marginBottom: 28,
  },
  userAvatar: {
    width: 52,
    height: 52,
    borderRadius: "50%",
    background: T.surface2,
    border: "1px solid " + T.border2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  userInitial: {
    fontSize: 20,
    fontWeight: 300,
    color: T.text,
    fontFamily: T.serif,
  },
  userName: {
    fontSize: 17,
    fontWeight: 400,
    color: T.text,
    fontFamily: T.serif,
    letterSpacing: "0.02em",
  },
  userEmail: {
    fontSize: 12,
    color: T.textSoft,
    marginTop: 2,
  },

  /* Cards */
  card: {
    background: T.surface,
    border: "1px solid " + T.border,
    borderRadius: 0,
    padding: "20px 22px",
    marginBottom: 12,
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  iconRow: {
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  iconWrap: {
    width: 38,
    height: 38,
    borderRadius: 0,
    background: "rgba(0,0,0,0.03)",
    border: "1px solid " + T.border,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: 500,
    color: T.text,
  },
  cardSub: {
    fontSize: 11,
    color: T.muted,
    marginTop: 2,
  },
  editBtn: {
    background: "transparent",
    border: "1px solid " + T.border,
    borderRadius: 0,
    color: T.text,
    padding: "6px 16px",
    cursor: "pointer",
    fontSize: 10,
    fontWeight: 500,
    letterSpacing: "0.15em",
    fontFamily: T.sans,
  },

  /* Password editing */
  pwBox: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 12,
    marginTop: 18,
    paddingTop: 18,
    borderTop: "1px solid " + T.border,
  },
  input: {
    width: "100%",
    background: T.bg,
    border: "1px solid " + T.border,
    borderRadius: 0,
    color: T.text,
    padding: "12px 42px 12px 14px",
    fontSize: 13,
    outline: "none",
    fontFamily: T.sans,
  },
  eyeBtn: {
    position: "absolute" as const,
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
  },
  pwActions: {
    display: "flex",
    gap: 10,
    marginTop: 4,
  },
  cancelBtn: {
    flex: 1,
    background: "transparent",
    border: "1px solid " + T.border,
    borderRadius: 0,
    color: T.textSoft,
    fontWeight: 500,
    padding: "11px",
    cursor: "pointer",
    fontSize: 11,
    letterSpacing: "0.12em",
    fontFamily: T.sans,
  },
  primaryBtn: {
    flex: 1,
    background: T.accent,
    border: "none",
    borderRadius: 0,
    color: T.bg,
    fontWeight: 500,
    padding: "11px",
    cursor: "pointer",
    fontSize: 11,
    letterSpacing: "0.12em",
    fontFamily: T.sans,
  },

  /* Danger zone */
  dangerSection: {
    marginTop: 32,
    paddingTop: 24,
    borderTop: "1px solid " + T.border,
  },
  dangerLabel: {
    fontSize: 10,
    color: T.danger,
    letterSpacing: "0.2em",
    fontWeight: 500,
    display: "block",
    marginBottom: 16,
  },
  deactivateBtn: {
    width: "100%",
    background: "transparent",
    border: "1px solid " + T.border2,
    borderRadius: 0,
    color: T.textSoft,
    fontWeight: 500,
    padding: "14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    letterSpacing: "0.15em",
    fontFamily: T.sans,
    marginBottom: 10,
  },
  deleteBtn: {
    width: "100%",
    background: T.dangerSoft,
    border: "1px solid " + T.dangerBorder,
    borderRadius: 0,
    color: T.danger,
    fontWeight: 500,
    padding: "14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    letterSpacing: "0.15em",
    fontFamily: T.sans,
  },

  /* Overlay */
  overlay: {
    position: "fixed" as const,
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    backdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 20,
  },
  alertCardDanger: {
    background: T.bg,
    border: "1px solid " + T.dangerBorder,
    borderRadius: 0,
    padding: "36px 28px",
    textAlign: "center" as const,
    width: "100%",
    maxWidth: 380,
  },
  alertCardWarn: {
    background: T.bg,
    border: "1px solid " + T.warnBorder,
    borderRadius: 0,
    padding: "36px 28px",
    textAlign: "center" as const,
    width: "100%",
    maxWidth: 380,
  },
  alertTitle: {
    fontWeight: 400,
    fontSize: 20,
    margin: "0 0 8px",
    fontFamily: T.serif,
    letterSpacing: "0.04em",
  },
  alertText: {
    color: T.textSoft,
    fontSize: 13,
    marginBottom: 24,
    lineHeight: 1.6,
  },
  alertActions: {
    display: "flex",
    gap: 10,
  },
  alertCancel: {
    flex: 1,
    background: "transparent",
    border: "1px solid " + T.border,
    borderRadius: 0,
    color: T.textSoft,
    fontWeight: 500,
    padding: "12px",
    cursor: "pointer",
    fontSize: 11,
    letterSpacing: "0.12em",
    fontFamily: T.sans,
  },
  alertDanger: {
    flex: 1,
    background: T.danger,
    border: "none",
    borderRadius: 0,
    color: "#ffffff",
    fontWeight: 500,
    padding: "12px",
    cursor: "pointer",
    fontSize: 11,
    letterSpacing: "0.12em",
    fontFamily: T.sans,
  },
  alertWarn: {
    flex: 1,
    background: T.accent,
    border: "none",
    borderRadius: 0,
    color: T.bg,
    fontWeight: 500,
    padding: "12px",
    cursor: "pointer",
    fontSize: 11,
    letterSpacing: "0.12em",
    fontFamily: T.sans,
  },
};